/**
 * Vimshottari dasha tree, computed locally from the Moon's exact birth
 * longitude via the standard classical proportional-division formula.
 *
 * This used to call VedAstro's `DasaAtRange` for the full Maha → Bhukti →
 * Antaram tree. Two real bugs made that unusable:
 *
 * 1. Querying `startTime = birthTime` silently DROPS the Maha dasha already
 *    partway through at birth (its true start predates the query window, so
 *    the API omits it rather than clipping it) — verified live: for a birth
 *    where Moon's dasha started ~3 months before birth, the response started
 *    at the SECOND dasha with no trace of the first. Since almost every
 *    birth lands partway through a dasha, this hit ~every chart, and it's
 *    often the currently-running period users care about most.
 * 2. Even for dashas it DID return, absolute dates drifted from the
 *    classical formula — by ~50 days at the second Maha dasha, growing to
 *    roughly a year by the sixth. Cross-checked against an independent
 *    reference site given the same birth details: computing the tree
 *    locally from VedAstro's own (trusted) Moon longitude via the standard
 *    formula matched that reference within a few days across the entire
 *    120-year span, while `DasaAtRange`'s own dates diverged far more.
 *
 * So the dates below are computed here, not fetched. `nature` (Good/Bad)
 * and the classical effect `description` VedAstro used to provide aren't
 * reproducible without its rule engine, so those fields are left empty —
 * a deliberate trade for correct dates.
 */

import { buildTime, callMethod } from './vedastro.js'
import { getAllPlanetData, memoized } from './kundliData.js'

const LEVEL_LABELS = { Dasa: 'Maha Dasha', Bhukti: 'Antar Dasha', Antaram: 'Pratyantar' }

// Fixed classical Vimshottari sequence + each lord's total Maha dasha years
// (sums to 120). Antardashas (and Pratyantars within them) follow this same
// cycle, rotated to start with their parent period's own lord.
const VIMSHOTTARI_ORDER = ['Ketu', 'Venus', 'Sun', 'Moon', 'Mars', 'Rahu', 'Jupiter', 'Saturn', 'Mercury']
const VIMSHOTTARI_YEARS = { Ketu: 7, Venus: 20, Sun: 6, Moon: 10, Mars: 7, Rahu: 18, Jupiter: 16, Saturn: 19, Mercury: 17 }
const YEAR_MS = 365.25 * 24 * 60 * 60 * 1000
const NAKSHATRA_SPAN = 360 / 27

const addYears = (date, years) => new Date(date.getTime() + years * YEAR_MS)

/** One period's children, in the classical rotated order, proportionally divided from `totalYears`. */
function buildSubPeriods(parentLord, start, totalYears, level, depth) {
  const startIdx = VIMSHOTTARI_ORDER.indexOf(parentLord)
  const seq = [...VIMSHOTTARI_ORDER.slice(startIdx), ...VIMSHOTTARI_ORDER.slice(0, startIdx)]
  let cursor = start
  return seq.map((lord) => {
    const years = (totalYears * VIMSHOTTARI_YEARS[lord]) / 120
    const periodStart = cursor
    const periodEnd = addYears(periodStart, years)
    cursor = periodEnd
    return {
      lord,
      level,
      levelLabel: LEVEL_LABELS[level],
      start: periodStart,
      end: periodEnd,
      duration: years >= 1 ? `${years.toFixed(2)} years` : `${(years * 12).toFixed(1)} months`,
      nature: null,
      description: '',
      subs: depth > 0 ? buildSubPeriods(lord, periodStart, years, 'Antaram', depth - 1) : [],
    }
  })
}

/**
 * The full Vimshottari tree for `years` (default 120, one whole cycle) from
 * the Moon's exact sidereal longitude at birth. Pure computation — one
 * shared `getAllPlanetData` call (already used elsewhere in the studio, so
 * usually free) instead of VedAstro's slow, buggy `DasaAtRange`.
 */
export async function getDashaTree({ dob, tob, location, years = 120 }, signal) {
  return memoized(`dasha-local|${dob}|${tob}|${location?.Name}|${years}`, async () => {
    const planets = await getAllPlanetData({ dob, tob, location }, signal)
    const moon = planets.find((p) => p.name === 'Moon')
    const moonLng = ((moon?.absDeg ?? 0) % 360 + 360) % 360
    const nakIdx = Math.floor(moonLng / NAKSHATRA_SPAN)
    const elapsedFraction = (moonLng - nakIdx * NAKSHATRA_SPAN) / NAKSHATRA_SPAN
    const startLord = VIMSHOTTARI_ORDER[nakIdx % 9]
    const firstMahaYears = VIMSHOTTARI_YEARS[startLord]
    const elapsedYears = elapsedFraction * firstMahaYears

    const [y, m, d] = String(dob).split('-').map(Number)
    const [hh, mm] = String(tob || '12:00').split(':').map(Number)
    const birthDate = new Date(y, m - 1, d, hh || 12, mm || 0)
    let cursor = addYears(birthDate, -elapsedYears)

    const tree = []
    let idx = nakIdx % 9
    let covered = 0
    while (covered < years) {
      const lord = VIMSHOTTARI_ORDER[idx % 9]
      const mahaYears = VIMSHOTTARI_YEARS[lord]
      const start = cursor
      const end = addYears(start, mahaYears)
      tree.push({
        lord,
        level: 'Dasa',
        levelLabel: LEVEL_LABELS.Dasa,
        start,
        end,
        duration: `${mahaYears} years`,
        nature: null,
        description: '',
        subs: buildSubPeriods(lord, start, mahaYears, 'Bhukti', 1),
      })
      cursor = end
      covered += mahaYears
      idx++
    }
    return tree
  })
}

/**
 * The currently-running dasha chain (lords only, no dates) via `DasaForNow` —
 * sub-second, so the UI can show "running now" while the full tree (pure
 * computation, but still awaits one planet-data call) settles in behind it.
 */
export async function getDashaNow({ dob, tob, location }, signal) {
  return memoized(`dasha-now|${dob}|${tob}|${location?.Name}`, async () => {
    const birthTime = buildTime(dob, tob, location)
    const payload = await callMethod('DasaForNow', { birthTime }, signal)
    const chain = []
    let node = payload
    while (node && typeof node === 'object') {
      const lord = Object.keys(node)[0]
      if (!lord) break
      const val = node[lord] || {}
      chain.push({ lord, level: val.Type, levelLabel: LEVEL_LABELS[val.Type] || val.Type })
      node = val.SubDasas
    }
    return chain
  })
}

/** Walk the tree to the periods running at `at` — [Maha, Bhukti, Antaram]. */
export function findRunningPath(tree, at = new Date()) {
  const path = []
  let nodes = tree
  while (Array.isArray(nodes) && nodes.length) {
    const cur = nodes.find((n) => n.start && n.end && n.start <= at && at < n.end)
    if (!cur) break
    path.push(cur)
    nodes = cur.subs
  }
  return path
}
